/**
 * PhotoCapture
 * --------------------------------------------------------------------------
 * Mobile-first photo capture for offline-friendly evidentiary attachments.
 *
 * Why this lives here
 *   On the ramp / hangar, ground staff need to document bird strikes,
 *   damage, or hazard scenes WITHOUT relying on connectivity. We compress
 *   client-side to keep payloads <200KB each so a 5-photo report stays
 *   well under the MongoDB 16MB doc cap once queued in IndexedDB.
 *
 * Behaviour
 *   - <input type="file" accept="image/*" capture="environment" multiple>
 *     opens the rear camera on iOS Safari + Android Chrome, falls back
 *     to a normal file picker on desktop.
 *   - Each picked file is downsampled via <canvas> to maxWidth=1280px
 *     and JPEG-encoded at quality=0.72. Resulting data URIs are passed
 *     to onChange() as base64 strings ready to embed in a JSON queue.
 *   - Hard cap: 5 photos / 1MB each (after compression) — guard rails
 *     against accidentally queueing huge payloads on a poor 3G uplink.
 *   - Optional geo-tagging (geoTag=true): captures GPS at the moment
 *     each photo is added and emits a parallel {lat, lon, accuracy,
 *     timestamp} array via onGeoChange. Decoupled from the photo
 *     `value`/`onChange` contract so non-geo consumers see no change.
 *     iOS Safari does NOT preserve EXIF GPS through <input capture>,
 *     so we use the JS Geolocation API as the source of truth.
 */
import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Camera, X, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const MAX_PHOTOS = 5;
const MAX_DIMENSION = 1280; // px on the longest side
const JPEG_QUALITY = 0.72;
const MAX_BYTES_AFTER_COMPRESS = 1024 * 1024; // 1MB
const GEO_TIMEOUT_MS = 4000;

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.onload = () => {
        const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
        // Approximate decoded size (base64 ~= 4/3 * raw bytes)
        const approxBytes = (dataUrl.length - 'data:image/jpeg;base64,'.length) * 0.75;
        if (approxBytes > MAX_BYTES_AFTER_COMPRESS) {
          reject(new Error(`Photo too large after compression (${Math.round(approxBytes / 1024)}KB)`));
          return;
        }
        resolve(dataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

// Best-effort GPS lookup. Resolves to null (never rejects) on:
// permission denied, browser unsupported, or timeout. We never block
// the photo capture on geolocation — if it fails we just record null.
const captureGeo = () =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null);
      return;
    }
    let settled = false;
    const t = setTimeout(() => {
      if (!settled) { settled = true; resolve(null); }
    }, GEO_TIMEOUT_MS);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? null,
          timestamp: new Date(pos.timestamp).toISOString(),
        });
      },
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 30000 }
    );
  });

export default function PhotoCapture({
  value = [],
  onChange,
  geoTag = false,
  geoValue = [],
  onGeoChange,
  maxPhotos = MAX_PHOTOS,
  testId = 'photo-capture',
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // Internal mirror: geoValue is the source of truth when the parent
  // owns it, otherwise we maintain our own. We always emit through
  // onGeoChange when geoTag is on so the parent can persist with photos.
  const geos = geoValue.length ? geoValue : value.map(() => null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = maxPhotos - value.length;
    if (room <= 0) {
      toast.error(`Maximum ${maxPhotos} photos`);
      return;
    }
    const toProcess = files.slice(0, room);
    setBusy(true);
    try {
      const compressed = [];
      const newGeos = [];
      for (const f of toProcess) {
        try {
          // Capture geo + compress in parallel — both use the same wall-clock
          // moment from the user's perspective, so the GPS reading is the
          // freshest possible at the time the photo was taken.
          const [dataUrl, geo] = await Promise.all([
            compressImage(f),
            geoTag ? captureGeo() : Promise.resolve(null),
          ]);
          compressed.push(dataUrl);
          newGeos.push(geo);
        } catch (err) {
          toast.error(`${f.name}: ${err.message}`);
        }
      }
      if (compressed.length) {
        onChange([...value, ...compressed]);
        if (geoTag && onGeoChange) {
          onGeoChange([...geos, ...newGeos]);
        }
      }
    } finally {
      setBusy(false);
      // Reset so picking the same file again still triggers onChange
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removePhoto = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
    if (geoTag && onGeoChange) {
      onGeoChange(geos.filter((_, i) => i !== idx));
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={busy || value.length >= maxPhotos}
        className="gap-2"
        data-testid={`${testId}-add-btn`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        {busy ? 'Compressing…' : `Add Photo (${value.length}/${maxPhotos})`}
      </Button>
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-2" data-testid={`${testId}-grid`}>
          {value.map((src, i) => {
            const g = geos[i];
            return (
              <div key={i} className="relative group rounded-md overflow-hidden border border-slate-700" data-testid={`${testId}-thumb-${i}`}>
                <img src={src} alt="" className="w-full h-20 object-cover" />
                {geoTag && g && (
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-black/70 px-1.5 py-0.5 flex items-center gap-1 text-[10px] text-emerald-300 tabular-nums font-mono"
                    data-testid={`${testId}-geo-${i}`}
                    title={`Accuracy: ±${Math.round(g.accuracy || 0)}m at ${g.timestamp}`}
                  >
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{g.lat.toFixed(4)}, {g.lon.toFixed(4)}</span>
                  </div>
                )}
                {geoTag && !g && (
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-black/70 px-1.5 py-0.5 flex items-center gap-1 text-[10px] text-amber-400"
                    data-testid={`${testId}-nogeo-${i}`}
                    title="Location not available — photo saved without geo-tag"
                  >
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">no GPS</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-rose-600 flex items-center justify-center transition-colors"
                  aria-label={`Remove photo ${i + 1}`}
                  data-testid={`${testId}-remove-${i}`}
                >
                  <X className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
